import { motion } from "framer-motion";
import { CalendarClock, Gauge, Wrench, ShoppingBag } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const features = [
  {
    icon: CalendarClock,
    title: "Track upcoming replacements",
    desc: "See which parts may need attention next, so maintenance does not come as a complete surprise.",
    testid: "feature-track",
  },
  {
    icon: Gauge,
    title: "Understand your parts",
    desc: "Our scoring engine evaluates and models different parts to help you understand their expected performance, value and suitability for your bike.",
    testid: "feature-understand",
  },
  {
    icon: Wrench,
    title: "Know when and what to replace",
    desc: "Get clearer guidance on when a part may need replacement and which options could offer better value for your riding needs.",
    testid: "feature-replace",
  },
  {
    icon: ShoppingBag,
    title: "Order through Parts E-Dukaan",
    desc: "Choose the required part and have it sent directly to a pre-verified mechanic from the Raahi network.",
    testid: "feature-edukaan",
  },
];

export default function Benefits() {
  return (
    <section
      data-testid="benefits-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#E7B459] mb-6">
            02 — What you get
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Clarity for every kilometre you ride.
          </h2>
        </motion.div>

        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
        >
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.testid}
                variants={fadeUp}
                data-testid={f.testid}
                className="group relative rounded-3xl bg-[#2D323D] border border-white/5 p-8 md:p-10 hover:border-[#E7B459]/40 transition-colors duration-300 hover:-translate-y-1 will-change-transform"
                style={{ transition: "border-color .3s, transform .3s" }}
              >
                <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1A1D24] border border-white/5 text-[#E7B459] group-hover:bg-[#D9544E] group-hover:text-[#F4F1ED] transition-colors duration-300">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight mb-3">
                  {f.title}
                </h3>
                <p className="text-base leading-relaxed text-[#F4F1ED]/65">
                  {f.desc}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
